import React, { useEffect, useMemo, useRef } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, GripVertical } from "lucide-react";
import { EditorProps } from "./EditorProps";
import { GlossaryData, GlossaryTerm, GlossaryTopic } from "@/services/admin/exercises.interfaces";

// Schema Zod para validação
const glossaryTermSchema = z.object({
  id: z.string(),
  term: z.string().min(1, "Termo é obrigatório"),
  definition: z.string().min(1, "Definição é obrigatória"),
  order: z.number().int().min(0)
});

const glossaryTopicSchema = z.object({
  id: z.string().min(1),
  title: z.string().min(1, "Título do tópico é obrigatório"),
  terms: z.array(glossaryTermSchema).min(1, "Tópico deve ter pelo menos um termo"),
  order: z.number().int().min(0).optional()
});

const glossarySchema = z.object({
  title: z.string().min(1, "Título é obrigatório"),
  topics: z.array(glossaryTopicSchema).min(1, "Adicione pelo menos um tópico")
});

type GlossaryFormData = z.infer<typeof glossarySchema>;

/**
 * Função de migração: converte dados antigos { title, terms[] } para novo formato { title, topics[] }
 * Sempre retorna o novo formato, mesmo se a entrada já estiver no formato novo
 */
function migrateLegacyGlossaryData(data: any): GlossaryFormData {
  try {
    // Se já está no formato novo, retornar como está (mas garantir estrutura correta)
    if (data && typeof data === 'object' && 'topics' in data && Array.isArray(data.topics)) {
      return {
        title: data.title || "",
        topics: data.topics.map((topic: any, index: number) => ({
          id: topic.id || `topic-${Date.now()}-${index}`,
          title: topic.title || "Geral",
          terms: Array.isArray(topic.terms) ? topic.terms.map((term: any, termIndex: number) => ({
            id: term.id || `term-${Date.now()}-${termIndex}`,
            term: term.term || "",
            definition: term.definition || "",
            order: term.order ?? termIndex
          })) : [],
          order: topic.order ?? index
        }))
      };
    }
    
    // Formato antigo: criar um único tópico com todos os termos
    if (data && typeof data === 'object' && 'terms' in data && Array.isArray(data.terms)) {
      console.log('[GlossaryEditor] Migrando dados antigos para novo formato');
      return {
        title: data.title || "",
        topics: [{
          id: `topic-${Date.now()}`,
          title: data.title || "Geral",
          terms: data.terms.map((term: any, index: number) => ({
            id: term.id || `term-${Date.now()}-${index}`,
            term: term.term || "",
            definition: term.definition || "",
            order: term.order ?? index
          })),
          order: 0
        }]
      };
    }
    
    // Fallback: estrutura vazia
    console.warn('[GlossaryEditor] Dados inválidos, usando estrutura vazia');
    return {
      title: "",
      topics: []
    };
  } catch (error) {
    console.error('[GlossaryEditor] Erro na migração:', error);
    // Fallback seguro em caso de erro
    return {
      title: "",
      topics: []
    };
  }
}

/**
 * Editor para exercícios do tipo Glossário Interativo
 * Usa React Hook Form com useFieldArray para gerenciar lista dinâmica de termos
 */
export const GlossaryEditor: React.FC<EditorProps> = ({
  value,
  onChange,
  onValidate,
  readOnly = false
}) => {
  // Parsear dados iniciais do exercício usando useMemo para evitar recálculo desnecessário
  // Usar uma referência estável para comparar os dados
  const valueDataRef = useRef<any>(null);
  const valueDataString = useMemo(() => {
    const stringified = JSON.stringify(value.data);
    // Só recalcular se os dados realmente mudaram
    if (valueDataRef.current !== stringified) {
      valueDataRef.current = stringified;
    }
    return valueDataRef.current;
  }, [value.data]);
  
  const initialData: GlossaryFormData = useMemo(() => {
    try {
      let data = value.data;
      
      // Se os dados são uma string JSON, fazer parse primeiro
      if (typeof data === 'string') {
        data = JSON.parse(data);
      }
      
      // Usar função de migração para garantir formato novo
      return migrateLegacyGlossaryData(data);
    } catch (error) {
      console.error("Error parsing glossary data:", error);
      // Fallback seguro
      return {
        title: "",
        topics: []
      };
    }
  }, [valueDataString]);

  const {
    control,
    watch,
    setValue,
    formState: { errors, isValid },
    reset
  } = useForm<GlossaryFormData>({
    resolver: zodResolver(glossarySchema),
    defaultValues: initialData,
    mode: 'onChange'
  });

  const { fields: topicFields, append: appendTopic, remove: removeTopic, move: moveTopic } = useFieldArray({
    control,
    name: "topics"
  });

  const watchedData = watch();

  // Notificar mudanças de validação
  useEffect(() => {
    if (onValidate) {
      onValidate(isValid);
    }
  }, [isValid, onValidate]);

  // Emitir mudanças quando o formulário é atualizado
  // Usar ref para onChange para evitar re-renderizações infinitas
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Usar debounce para evitar chamadas excessivas ao onChange
  const debounceTimeoutRef = useRef<NodeJS.Timeout>();
  
  useEffect(() => {
    // Limpar timeout anterior se existir
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    
    // Só emitir mudanças se houver dados válidos
    if (watchedData.title || (watchedData.topics && watchedData.topics.length > 0)) {
      debounceTimeoutRef.current = setTimeout(() => {
        const glossaryData: GlossaryData = {
          title: watchedData.title,
          topics: watchedData.topics.map((topic, topicIndex) => ({
            id: topic.id,
            title: topic.title,
            terms: topic.terms.map((term, termIndex) => ({
              id: term.id,
              term: term.term,
              definition: term.definition,
              order: term.order ?? termIndex
            })),
            order: topic.order ?? topicIndex
          }))
        };

        onChangeRef.current({
          ...value,
          type: "glossary_display" as any,
          data: glossaryData
        });
      }, 300); // Debounce de 300ms
    }
    
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [watchedData, value]);

  // Resetar formulário quando initialData muda externamente (não durante edição)
  // Usar string JSON para comparação simples e eficiente
  // Só resetar se os dados realmente mudaram E não estamos editando
  const prevInitialDataRef = useRef<string>();
  const isEditingRef = useRef(false);
  const lastResetRef = useRef<string>();
  
  useEffect(() => {
    const current = JSON.stringify(initialData);
    // Só resetar se:
    // 1. Os dados realmente mudaram
    // 2. Não estamos editando ativamente
    // 3. Não acabamos de resetar com esses mesmos dados
    if (prevInitialDataRef.current !== current && 
        !isEditingRef.current && 
        lastResetRef.current !== current) {
      prevInitialDataRef.current = current;
      lastResetRef.current = current;
      reset(initialData);
    }
  }, [initialData, reset]);

  // Marcar que estamos editando quando watchedData muda
  useEffect(() => {
    if (watchedData.title || (watchedData.topics && watchedData.topics.length > 0)) {
      isEditingRef.current = true;
      // Resetar flag após um pequeno delay para permitir reset em mudanças externas reais
      const timer = setTimeout(() => {
        isEditingRef.current = false;
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [watchedData]);

  const handleAddTopic = () => {
    const newTopic: GlossaryTopic = {
      id: `topic-${Date.now()}`,
      title: "",
      terms: [{
        id: `term-${Date.now()}`,
        term: "",
        definition: "",
        order: 0
      }],
      order: topicFields.length
    };
    appendTopic(newTopic);
  };

  const handleRemoveTopic = (index: number) => {
    if (topicFields.length > 1) {
      removeTopic(index);
    }
  };

  const handleMoveTopicUp = (index: number) => {
    if (index > 0) {
      moveTopic(index, index - 1);
    }
  };

  const handleMoveTopicDown = (index: number) => {
    if (index < topicFields.length - 1) {
      moveTopic(index, index + 1);
    }
  };

  const handleAddTerm = (topicIndex: number) => {
    const currentTopics = [...(watchedData.topics || [])];
    const currentTopic = currentTopics[topicIndex];
    if (!currentTopic) return;
    
    const newTerm: GlossaryTerm = {
      id: `term-${Date.now()}`,
      term: "",
      definition: "",
      order: currentTopic.terms.length
    };
    
    currentTopics[topicIndex] = {
      ...currentTopic,
      terms: [...currentTopic.terms, newTerm]
    };
    
    setValue(`topics`, currentTopics, { shouldValidate: true });
  };

  const handleRemoveTerm = (topicIndex: number, termIndex: number) => {
    const currentTopics = [...(watchedData.topics || [])];
    const currentTopic = currentTopics[topicIndex];
    if (!currentTopic || currentTopic.terms.length <= 1) return;
    
    currentTopics[topicIndex] = {
      ...currentTopic,
      terms: currentTopic.terms.filter((_, idx) => idx !== termIndex)
    };
    
    setValue(`topics`, currentTopics, { shouldValidate: true });
  };

  return (
    <div className="space-y-6">
      {/* Campo de título do glossário */}
      <div className="space-y-2">
        <Label htmlFor="glossary-title">Título do Glossário</Label>
        <Input
          id="glossary-title"
          {...control.register("title")}
          placeholder="Ex: Glossário de Programação"
          disabled={readOnly}
          className={errors.title ? "border-red-500" : ""}
        />
        {errors.title && (
          <p className="text-sm text-red-600">{errors.title.message}</p>
        )}
      </div>

      {/* Lista de tópicos */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Label>Tópicos do Glossário</Label>
          {!readOnly && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleAddTopic}
            >
              <Plus className="h-4 w-4 mr-2" />
              Adicionar Tópico
            </Button>
          )}
        </div>

        {topicFields.length === 0 && (
          <p className="text-sm text-gray-500">
            Nenhum tópico adicionado. Clique em "Adicionar Tópico" para começar.
          </p>
        )}

        {topicFields.map((topicField, topicIndex) => {
          const topic = watchedData.topics?.[topicIndex];
          const terms = topic?.terms || [];
          
          return (
            <div
              key={topicField.id}
              className="border rounded-lg p-4 space-y-4 bg-gray-50"
            >
              {/* Header do tópico */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 flex-1">
                  <div className="flex-1">
                    <Label htmlFor={`topic-title-${topicIndex}`}>Título do Tópico</Label>
                    <Input
                      id={`topic-title-${topicIndex}`}
                      {...control.register(`topics.${topicIndex}.title`)}
                      placeholder="Ex: Conceitos Básicos"
                      disabled={readOnly}
                      className={errors.topics?.[topicIndex]?.title ? "border-red-500" : ""}
                    />
                    {errors.topics?.[topicIndex]?.title && (
                      <p className="text-sm text-red-600">
                        {errors.topics[topicIndex]?.title?.message}
                      </p>
                    )}
                  </div>
                  <span className="text-sm text-gray-500">
                    {terms.length} {terms.length === 1 ? 'termo' : 'termos'}
                  </span>
                  {!readOnly && (
                    <div className="flex gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => handleMoveTopicUp(topicIndex)}
                        disabled={topicIndex === 0}
                        className="h-6 w-6"
                      >
                        <GripVertical className="h-4 w-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => handleMoveTopicDown(topicIndex)}
                        disabled={topicIndex === topicFields.length - 1}
                        className="h-6 w-6"
                      >
                        <GripVertical className="h-4 w-4 rotate-90" />
                      </Button>
                    </div>
                  )}
                </div>
                {!readOnly && topicFields.length > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => handleRemoveTopic(topicIndex)}
                    className="text-red-600 hover:text-red-700 ml-2"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>

              {/* Lista de termos do tópico */}
              <div className="space-y-3 pl-4 border-l-2 border-gray-300">
                <div className="flex items-center justify-between">
                  <Label className="text-sm">Termos</Label>
                  {!readOnly && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handleAddTerm(topicIndex)}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Adicionar Termo
                    </Button>
                  )}
                </div>

                {terms.map((term, termIndex) => (
                  <div
                    key={term.id || `term-${termIndex}`}
                    className="border rounded p-3 space-y-2 bg-white"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-gray-600">
                        Termo {termIndex + 1}
                      </span>
                      {!readOnly && terms.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveTerm(topicIndex, termIndex)}
                          className="h-5 w-5 text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                    <div className="space-y-2">
                      <div>
                        <Label htmlFor={`term-${topicIndex}-${termIndex}`} className="text-xs">Termo</Label>
                        <Input
                          id={`term-${topicIndex}-${termIndex}`}
                          {...control.register(`topics.${topicIndex}.terms.${termIndex}.term`)}
                          placeholder="Ex: API"
                          disabled={readOnly}
                          className={errors.topics?.[topicIndex]?.terms?.[termIndex]?.term ? "border-red-500" : ""}
                        />
                        {errors.topics?.[topicIndex]?.terms?.[termIndex]?.term && (
                          <p className="text-xs text-red-600">
                            {errors.topics[topicIndex]?.terms?.[termIndex]?.term?.message}
                          </p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor={`definition-${topicIndex}-${termIndex}`} className="text-xs">Definição</Label>
                        <Textarea
                          id={`definition-${topicIndex}-${termIndex}`}
                          {...control.register(`topics.${topicIndex}.terms.${termIndex}.definition`)}
                          placeholder="Ex: Application Programming Interface"
                          disabled={readOnly}
                          rows={2}
                          className={errors.topics?.[topicIndex]?.terms?.[termIndex]?.definition ? "border-red-500" : ""}
                        />
                        {errors.topics?.[topicIndex]?.terms?.[termIndex]?.definition && (
                          <p className="text-xs text-red-600">
                            {errors.topics[topicIndex]?.terms?.[termIndex]?.definition?.message}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        {errors.topics && typeof errors.topics === 'object' && 'message' in errors.topics && (
          <p className="text-sm text-red-600">{errors.topics.message as string}</p>
        )}
      </div>
    </div>
  );
};

